import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { sanitizeTextForWinAnsiPdf } from "@/utils/pdfWinAnsiText";

const PAGE_MARGIN = 50;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * PAGE_MARGIN;

const GRAY_TEXT = rgb(0.42, 0.45, 0.51);
const BLACK_TEXT = rgb(0.11, 0.11, 0.11);
const CARD_BG = rgb(0.98, 0.98, 0.985);
const CARD_BORDER = rgb(0.85, 0.87, 0.9);
const SUCCESS_BG = rgb(0.92, 0.98, 0.94);
const SUCCESS_BORDER = rgb(0.67, 0.9, 0.75);
const SUCCESS_TEXT = rgb(0.12, 0.52, 0.22);
const BADGE_BG = rgb(0.87, 0.97, 0.9);
const BADGE_TEXT = rgb(0.12, 0.52, 0.22);

const ORDERED_KEYS = [
  "companyName",
  "companyAddress",
  "dateOfIncorporation",
  "registrationNumber",
  "companyType",
  "companyStatus",
  "indirectVerificationSource",
  "natureOfBusiness",
  "authorizedCapital",
  "paidUpCapital",
  "email",
  "phoneNumber",
] as const;

const LABEL_OVERRIDES: Partial<Record<(typeof ORDERED_KEYS)[number], string>> = {
  companyName: "COMPANY NAME",
  companyAddress: "COMPANY ADDRESS",
  dateOfIncorporation: "DATE OF REGISTRATION OF CERTIFICATE",
  indirectVerificationSource: "VERIFICATION SOURCE",
};

function formatLabelFromKey(key: string): string {
  return key.replace(/([A-Z])/g, " $1").trim().toUpperCase();
}

type Row = { label: string; value: string };

function collectRows(data: Record<string, unknown>): Row[] {
  const skip = new Set(["_verificationSource"]);
  const orderedSet = new Set<string>(ORDERED_KEYS);
  const rows: Row[] = [];

  for (const key of ORDERED_KEYS) {
    if (skip.has(key)) continue;
    const v = data[key];
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object") continue;
    rows.push({
      label: LABEL_OVERRIDES[key] ?? formatLabelFromKey(key),
      value: String(v),
    });
  }

  for (const key of Object.keys(data)) {
    if (skip.has(key) || orderedSet.has(key)) continue;
    const v = data[key];
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object") continue;
    rows.push({ label: formatLabelFromKey(key), value: String(v) });
  }

  return rows;
}

export async function downloadSystemVerificationPdf(verificationData: Record<string, unknown>): Promise<void> {
  const source = verificationData._verificationSource;
  const generatedAt = new Date();
  const rows = collectRows(verificationData);

  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - PAGE_MARGIN;

  let logoImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | null = null;
  let logoDims = { width: 0, height: 0 };
  try {
    const logoBytes = await fetch("/GT logo.png").then((r) => r.arrayBuffer());
    logoImage = await pdfDoc.embedPng(logoBytes);
    const LOGO_H = 60;
    logoDims = { width: logoImage.width * (LOGO_H / logoImage.height), height: LOGO_H };
  } catch {
    // skip
  }

  const drawText = (
    text: string,
    x: number,
    startY: number,
    options: {
      size?: number;
      font?: typeof helvetica;
      color?: typeof BLACK_TEXT;
      maxWidth?: number;
      lineHeight?: number;
    } = {}
  ): number => {
    const {
      size = 10,
      font = helvetica,
      color = BLACK_TEXT,
      maxWidth = CONTENT_WIDTH,
      lineHeight = size * 1.5,
    } = options;

    const safe = sanitizeTextForWinAnsiPdf(text);
    const words = safe.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, size);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    let currentY = startY;
    for (const line of lines) {
      page.drawText(line, { x, y: currentY, size, font, color });
      currentY -= lineHeight;
    }

    return currentY;
  };

  const checkNewPage = (requiredSpace: number) => {
    if (y - requiredSpace < PAGE_MARGIN) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - PAGE_MARGIN;
    }
  };

  const drawStatusBadge = (status: string, x: number, topY: number) => {
    const text = sanitizeTextForWinAnsiPdf(status).toUpperCase();
    const size = 9;
    const padX = 8;
    const padY = 4;
    const textW = helveticaBold.widthOfTextAtSize(text, size);
    const w = textW + padX * 2;
    const h = size + padY * 2;
    page.drawRectangle({
      x,
      y: topY - h,
      width: w,
      height: h,
      color: BADGE_BG,
      borderColor: SUCCESS_BORDER,
      borderWidth: 0.6,
    });
    page.drawText(text, { x: x + padX, y: topY - h + padY, size, font: helveticaBold, color: BADGE_TEXT });
    return { width: w, height: h };
  };

  checkNewPage(100);
  page.drawText("System Verification Result", { x: PAGE_MARGIN, y, size: 16, font: helveticaBold, color: BLACK_TEXT });
  if (logoImage) {
    const topPad = 4;
    page.drawImage(logoImage, {
      x: PAGE_WIDTH - PAGE_MARGIN - logoDims.width,
      y: y + topPad - logoDims.height,
      width: logoDims.width,
      height: logoDims.height,
    });
  }

  const titleRowH = Math.max(24, logoImage ? logoDims.height : 0) + 12;
  y -= titleRowH;

  const bannerH = 44;
  page.drawRectangle({
    x: PAGE_MARGIN,
    y: y - bannerH,
    width: CONTENT_WIDTH,
    height: bannerH,
    color: SUCCESS_BG,
    borderColor: SUCCESS_BORDER,
    borderWidth: 1,
  });
  const iconR = 8;
  const iconCx = PAGE_MARGIN + 18;
  const iconCy = y - bannerH / 2;
  page.drawCircle({ x: iconCx, y: iconCy, size: iconR, color: rgb(0.84, 0.96, 0.88), borderColor: SUCCESS_BORDER, borderWidth: 1 });

  page.drawLine({ start: { x: iconCx - 3.5, y: iconCy - 0.5 }, end: { x: iconCx - 1.0, y: iconCy - 3.0 }, thickness: 1.6, color: SUCCESS_TEXT });
  page.drawLine({ start: { x: iconCx - 1.0, y: iconCy - 3.0 }, end: { x: iconCx + 4.0, y: iconCy + 3.0 }, thickness: 1.6, color: SUCCESS_TEXT });
  page.drawText("System verification completed successfully!", {
    x: PAGE_MARGIN + 36,
    y: y - 27,
    size: 11,
    font: helveticaBold,
    color: SUCCESS_TEXT,
  });
  y -= bannerH + 18;

  const cardX = PAGE_MARGIN;
  const cardW = CONTENT_WIDTH;
  const labelSize = 8;
  const valueSize = 11;
  const labelGap = 6;
  const blockGap = 18;
  const cardPad = 18;
  const cardTopY = y;
  const cardMinBottomY = PAGE_MARGIN;
  const drawCardContainer = (topY: number) => {
    const bottomY = cardMinBottomY;
    page.drawRectangle({
      x: cardX,
      y: bottomY,
      width: cardW,
      height: topY - bottomY,
      color: CARD_BG,
      borderColor: CARD_BORDER,
      borderWidth: 1,
    });
    return { topY, bottomY };
  };

  const checkNewPageInCard = (requiredSpace: number) => {
    if (curY - requiredSpace < PAGE_MARGIN + 12) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - PAGE_MARGIN;
      drawCardContainer(y);
      curY = y - cardPad;
    }
  };

  drawCardContainer(cardTopY);
  let curY = cardTopY - cardPad;

  const drawField = (label: string, value: string) => {
    const minNeed = labelSize * 1.6 + valueSize * 1.7 + blockGap;
    checkNewPageInCard(minNeed);
    page.drawText(sanitizeTextForWinAnsiPdf(label.toUpperCase()), {
      x: cardX + cardPad,
      y: curY,
      size: labelSize,
      font: helveticaBold,
      color: GRAY_TEXT,
    });
    curY -= labelSize + labelGap;
    curY = drawText(value, cardX + cardPad, curY, {
      size: valueSize,
      font: helveticaBold,
      color: BLACK_TEXT,
      maxWidth: cardW - cardPad * 2,
      lineHeight: valueSize * 1.5,
    });
    curY -= blockGap;
  };

  drawField("GENERATED AT", generatedAt.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }));

  const sourceText = source != null && source !== "" ? String(source) : "N/A";
  drawField("VERIFICATION SOURCE", sourceText);

  checkNewPageInCard(40);
  page.drawText("VERIFIED COMPANY DETAILS", {
    x: cardX + cardPad,
    y: curY,
    size: 12,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  curY -= 20;

  for (const { label, value } of rows) {
    if (label === "COMPANY STATUS") {
      checkNewPageInCard(40);
      page.drawText(sanitizeTextForWinAnsiPdf(label.toUpperCase()), {
        x: cardX + cardPad,
        y: curY,
        size: labelSize,
        font: helveticaBold,
        color: GRAY_TEXT,
      });
      curY -= labelSize + labelGap;
      const badge = drawStatusBadge(value || "N/A", cardX + cardPad, curY + 12);
      curY -= badge.height + blockGap;
      continue;
    }
    drawField(label, value);
  }

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const stamp = generatedAt.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const a = document.createElement("a");
  a.href = url;
  a.download = `system-verification-${stamp}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
